import {
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignJustify as AlignJustifyIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignRight as AlignRightIcon,
  FormatQuote as BlockquoteIcon,
  FormatBold as BoldIcon,
  FormatListBulleted as BulletListIcon,
  ExpandMore as ChevronIcon,
  DataObject as CodeBlockIcon,
  Code as CodeIcon,
  LooksOne as Heading1Icon,
  LooksTwo as Heading2Icon,
  Looks3 as Heading3Icon,
  Highlight as HighlightIcon,
  HorizontalRule as HorizontalRuleIcon,
  FormatItalic as ItalicIcon,
  FormatListNumbered as OrderedListIcon,
  FormatStrikethrough as StrikethroughIcon,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  FormatUnderlined as UnderlineIcon,
} from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import type { Editor } from "@tiptap/react";
import { type MouseEvent, useState } from "react";

import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  DropdownTrigger,
  dropdownMenuProps,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
} from "Shared/TextEditor/editorStyles";

interface EditorToolbarProps {
  editor: Editor;
}

export const EditorToolbar = ({
  editor,
}: EditorToolbarProps): React.ReactNode => {
  const { t } = useResource();
  const [headingAnchor, setHeadingAnchor] = useState<HTMLElement | null>(null);
  const [listAnchor, setListAnchor] = useState<HTMLElement | null>(null);

  const isAnyHeadingActive = editor.isActive("heading");
  const isAnyListActive =
    editor.isActive("bulletList") || editor.isActive("orderedList");

  const activeHeadingLabel = editor.isActive("heading", { level: 1 })
    ? t(Resources.TextEditor.Heading1)
    : editor.isActive("heading", { level: 2 })
      ? t(Resources.TextEditor.Heading2)
      : editor.isActive("heading", { level: 3 })
        ? t(Resources.TextEditor.Heading3)
        : t(Resources.TextEditor.Paragraph);

  const activeListIcon = editor.isActive("orderedList") ? (
    <OrderedListIcon />
  ) : (
    <BulletListIcon />
  );

  const handleHeadingSelect = (level: 1 | 2 | 3 | null) => {
    if (level === null) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level }).run();
    }
    setHeadingAnchor(null);
  };

  const handleListSelect = (type: "bulletList" | "orderedList") => {
    if (type === "bulletList") {
      editor.chain().focus().toggleBulletList().run();
    } else {
      editor.chain().focus().toggleOrderedList().run();
    }
    setListAnchor(null);
  };

  return (
    <Toolbar>
      {/* Heading dropdown */}
      <DropdownTrigger
        type="button"
        $isActive={isAnyHeadingActive}
        onClick={(e: MouseEvent<HTMLButtonElement>) =>
          setHeadingAnchor(e.currentTarget)
        }
        title={activeHeadingLabel}
      >
        {editor.isActive("heading", { level: 1 }) ? (
          <Heading1Icon />
        ) : editor.isActive("heading", { level: 2 }) ? (
          <Heading2Icon />
        ) : editor.isActive("heading", { level: 3 }) ? (
          <Heading3Icon />
        ) : (
          <Heading1Icon />
        )}
        <ChevronIcon />
      </DropdownTrigger>
      <Menu
        anchorEl={headingAnchor}
        open={Boolean(headingAnchor)}
        onClose={() => setHeadingAnchor(null)}
        slotProps={dropdownMenuProps}
      >
        <MenuItem
          selected={!isAnyHeadingActive}
          onClick={() => handleHeadingSelect(null)}
        >
          {t(Resources.TextEditor.Paragraph)}
        </MenuItem>
        <MenuItem
          selected={editor.isActive("heading", { level: 1 })}
          onClick={() => handleHeadingSelect(1)}
        >
          <Heading1Icon />
          {t(Resources.TextEditor.Heading1)}
        </MenuItem>
        <MenuItem
          selected={editor.isActive("heading", { level: 2 })}
          onClick={() => handleHeadingSelect(2)}
        >
          <Heading2Icon />
          {t(Resources.TextEditor.Heading2)}
        </MenuItem>
        <MenuItem
          selected={editor.isActive("heading", { level: 3 })}
          onClick={() => handleHeadingSelect(3)}
        >
          <Heading3Icon />
          {t(Resources.TextEditor.Heading3)}
        </MenuItem>
      </Menu>

      {/* List dropdown */}
      <DropdownTrigger
        type="button"
        $isActive={isAnyListActive}
        onClick={(e: MouseEvent<HTMLButtonElement>) =>
          setListAnchor(e.currentTarget)
        }
      >
        {activeListIcon}
        <ChevronIcon />
      </DropdownTrigger>
      <Menu
        anchorEl={listAnchor}
        open={Boolean(listAnchor)}
        onClose={() => setListAnchor(null)}
        slotProps={dropdownMenuProps}
      >
        <MenuItem
          selected={editor.isActive("bulletList")}
          onClick={() => handleListSelect("bulletList")}
        >
          <BulletListIcon />
          {t(Resources.TextEditor.BulletList)}
        </MenuItem>
        <MenuItem
          selected={editor.isActive("orderedList")}
          onClick={() => handleListSelect("orderedList")}
        >
          <OrderedListIcon />
          {t(Resources.TextEditor.OrderedList)}
        </MenuItem>
      </Menu>

      {/* Blockquote */}
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        $isActive={editor.isActive("blockquote")}
        title={t(Resources.TextEditor.Blockquote)}
      >
        <BlockquoteIcon />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Inline formatting */}
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        $isActive={editor.isActive("bold")}
        title={t(Resources.TextEditor.Bold)}
      >
        <BoldIcon />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        $isActive={editor.isActive("italic")}
        title={t(Resources.TextEditor.Italic)}
      >
        <ItalicIcon />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        $isActive={editor.isActive("strike")}
        title={t(Resources.TextEditor.Strikethrough)}
      >
        <StrikethroughIcon />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().toggleCode().run()}
        $isActive={editor.isActive("code")}
        title={t(Resources.TextEditor.Code)}
      >
        <CodeIcon />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        $isActive={editor.isActive("underline")}
        title={t(Resources.TextEditor.Underline)}
      >
        <UnderlineIcon />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        $isActive={editor.isActive("highlight")}
        title={t(Resources.TextEditor.Highlight)}
      >
        <HighlightIcon />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Superscript / Subscript */}
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().toggleSuperscript().run()}
        $isActive={editor.isActive("superscript")}
        title={t(Resources.TextEditor.Superscript)}
      >
        <SuperscriptIcon />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().toggleSubscript().run()}
        $isActive={editor.isActive("subscript")}
        title={t(Resources.TextEditor.Subscript)}
      >
        <SubscriptIcon />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Text alignment */}
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        $isActive={editor.isActive({ textAlign: "left" })}
        title={t(Resources.TextEditor.AlignLeft)}
      >
        <AlignLeftIcon />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        $isActive={editor.isActive({ textAlign: "center" })}
        title={t(Resources.TextEditor.AlignCenter)}
      >
        <AlignCenterIcon />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        $isActive={editor.isActive({ textAlign: "right" })}
        title={t(Resources.TextEditor.AlignRight)}
      >
        <AlignRightIcon />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        $isActive={editor.isActive({ textAlign: "justify" })}
        title={t(Resources.TextEditor.AlignJustify)}
      >
        <AlignJustifyIcon />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Block elements */}
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        $isActive={editor.isActive("codeBlock")}
        title={t(Resources.TextEditor.CodeBlock)}
      >
        <CodeBlockIcon />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title={t(Resources.TextEditor.HorizontalRule)}
      >
        <HorizontalRuleIcon />
      </ToolbarButton>
    </Toolbar>
  );
};
